// ----- Phase sibling-selector-toolbar (TODO-058 Part B) -----
//
// Compact dropdown rendered inside the CompilerToolbar that lets a
// publisher switch between sequences of the SAME application
// (NdaNumber + TenantId). Selecting a prior sequence layers its
// documents into the section list alongside the current sequence's
// documents (rendered in muted style by DocumentList).
//
// Backed by GET /api/v1/submissions/:id/siblings (Phase TODO-058 Part A).
//
// VISIBILITY: the entire control hides itself when `siblings.length < 2`
// — a dropdown with only the current sequence has no purpose.

import { type CSSProperties, type ReactElement, type ChangeEvent } from 'react';
import type { SubmissionSibling } from '@/api/v2Client';

export interface SiblingSequenceSelectorProps {
  /** Full list of siblings (current sequence included, IsCurrent=true). */
  readonly siblings: readonly SubmissionSibling[];
  /**
   * Selected prior sequence id. `null` means "current sequence only —
   * no overlay". The current sequence is never the selected value here
   * (it's always shown by default).
   */
  readonly selectedSiblingId: string | null;
  /** Called when the user picks a sibling (or clears back to null). */
  readonly onChange: (siblingId: string | null) => void;
}

function formatSeq(n: number | string): string {
  // Sequences are stored as 4-digit strings in Submissions; this is
  // defensive against either shape.
  const s = String(n);
  return s.padStart(4, '0');
}

function labelFor(s: SubmissionSibling): string {
  const seq = `Seq ${formatSeq(s.SequenceNumber)}`;
  const desc = s.SequenceDescription?.trim() ?? '';
  const docs = `${s.DocumentCount} ${Number(s.DocumentCount) === 1 ? 'doc' : 'docs'}`;
  const tail = s.IsCurrent ? ' (current)' : '';
  return desc.length > 0
    ? `${seq} — ${desc} — ${docs}${tail}`
    : `${seq} — ${docs}${tail}`;
}

const selectStyle: CSSProperties = {
  fontSize: 12,
  padding: '3px 6px',
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 4,
  color: 'var(--text)',
  cursor: 'pointer',
  maxWidth: '100%',
  minWidth: 0,
  flex: '1 1 180px',
};

const labelStyle: CSSProperties = {
  fontSize: 11,
  color: 'var(--text-muted)',
  marginRight: 4,
};

/**
 * Sibling-sequence selector. Hides itself when there are fewer than 2
 * siblings — a single-row dropdown is noise.
 */
export function SiblingSequenceSelector({
  siblings,
  selectedSiblingId,
  onChange,
}: SiblingSequenceSelectorProps): ReactElement | null {
  if (siblings.length < 2) return null;

  // Render order: SequenceNumber asc (server already does this, but the
  // component shouldn't rely on caller ordering).
  const ordered = [...siblings].sort(
    (a, b) => Number(a.SequenceNumber) - Number(b.SequenceNumber),
  );

  const handleChange = (e: ChangeEvent<HTMLSelectElement>): void => {
    const v = e.target.value;
    onChange(v === '' ? null : v);
  };

  return (
    <span
      data-testid="sibling-sequence-selector"
      style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 4, maxWidth: '100%', minWidth: 0 }}
    >
      <label htmlFor="sibling-sequence-select" style={labelStyle}>
        Overlay prior:
      </label>
      <select
        id="sibling-sequence-select"
        data-testid="sibling-sequence-select"
        aria-label="Overlay documents from a prior sequence"
        value={selectedSiblingId ?? ''}
        onChange={handleChange}
        style={selectStyle}
      >
        <option value="">— none —</option>
        {ordered.map((s) => (
          <option
            key={s.Id}
            value={s.Id}
            disabled={s.IsCurrent}
            data-testid={`sibling-option-${formatSeq(s.SequenceNumber)}`}
          >
            {labelFor(s)}
          </option>
        ))}
      </select>
    </span>
  );
}
